import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  useTheme,
  useMediaQuery,
  styled,
  keyframes,
  Card,
  CardContent
} from '@mui/material';
import { motion } from 'framer-motion';
import { alpha } from '@mui/material/styles' ;

// Keyframe Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const AnimatedBox = styled(Box)(({ theme }) => ({
  animation: `${fadeIn} 1s ease forwards`,
  opacity: 0,
}));

// SIZING UPDATE: Updated card with fixed dimensions.
const EnhancedFeatureCard = styled(Card)(({ theme }) => ({
  width: '300px', // Static width as requested
  height: '200px', // Static height as requested
  margin: '0 auto', // Center the card within the grid column
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: theme.shadows[10],
  },
  backgroundColor: alpha(theme.palette.background.default, 0.6),
  borderRadius: theme.shape.borderRadius * 2,
}));

const FeatureIcon = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  marginBottom: theme.spacing(1.5),
  textShadow: `2px 2px 8px ${alpha(theme.palette.action.active, 0.1)}`,
}));

const FeatureTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const FeatureDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
  flexGrow: 1, // Allows description to take available space
}));


// SEO Structured Data for your organization
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Copper Boss Technology",
  "url": "https://www.yourwebsite.com/about", // <-- IMPORTANT: Replace with your actual About Us page URL
  "logo": "https://www.yourwebsite.com/logo.png", // <-- IMPORTANT: Replace with your logo URL
  "description": "Copper Boss Technology provides innovative and reliable IT solutions, including system monitoring, hardware repairs, cybersecurity, and custom website development to drive business success in Madurai.",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "123 Tech Avenue", // <-- IMPORTANT: Replace with your address
    "addressLocality": "Madurai",
    "addressRegion": "Tamil Nadu",
    "postalCode": "625001",
    "addressCountry": "IN"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "Customer Service",
    "telephone": "+91-123-456-7890", // <-- IMPORTANT: Replace with your phone number
    "email": "support@copperbosstech.com" // <-- IMPORTANT: Replace with your email
  },
  "sameAs": [
    "https://www.facebook.com/yourprofile", // <-- IMPORTANT: Replace with your social media links
    "https://www.twitter.com/yourprofile",
    "https://www.linkedin.com/company/yourprofile"
  ]
};

// ANIMATION UPDATE: Define animation variants for framer-motion.
const cardContainerVariant = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2, // This creates the "one-by-one" effect
    },
  },
};

const cardItemVariant = {
  hidden: { opacity: 0, y: 50 }, // Start hidden, 50px down
  visible: { opacity: 1, y: 0 }, // Animate to visible, original position
};


const AboutUs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <>
      {/* SEO Head Tags using native React 19 features */}
      <title>Copper Boss Technology | Expert IT Solutions in Virudhunagar | Best Laptop Service in Virudhunagar</title>
      <meta 
        name="description" 
        content="Copper Boss Technology – Professional Laptop Service, IT Support, and Custom PC Solutions
At Copper Boss Technology, we provide professional laptop repair services, custom software development, website design, and reliable IT support for businesses and individuals. Our goal is to deliver fast, affordable, and high-quality technology solutions that keep you productive and ahead of the competition.

Professional Laptop Service
We specialize in laptop repair, upgrades, and maintenance for all major brands. From hardware replacement to performance optimization, our skilled technicians ensure your laptop runs at its best. We also offer premium laptop builds designed for business, creative, or gaming needs.

Software & Website Development
Our team creates custom software solutions and modern, SEO-friendly websites that help businesses grow. We focus on fast-loading, mobile-responsive designs with strong security features, ensuring your online presence is professional and competitive.

Professional IT Support
We provide comprehensive IT support services, including network setup, cybersecurity protection, system monitoring, and data backup solutions. Whether you are a small business or a growing enterprise, we ensure your IT infrastructure runs smoothly.

Premium Laptop & Custom PC Builds
Need more power? We design and build custom high-performance PCs for gaming, graphic design, video editing, and professional workloads. Our tailored builds use only premium components for maximum speed, durability, and efficiency.

At Copper Boss Technology, we combine technical expertise, customer-focused service, and innovative solutions to deliver the best results for your technology needs. Whether you need a quick laptop fix, a secure business website, or a custom PC build, we are your trusted IT partner." 
      />
      <link rel="canonical" href="https://www.yourwebsite.com/about" /> {/* <-- IMPORTANT: Replace with your actual URL */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.yourwebsite.com/about" /> {/* <-- IMPORTANT: Replace with your actual URL */}
      <meta property="og:title" content="Copper Boss Technology | Expert IT Solutions in Virudhunagar | Best Laptop Service in Virudhunagar" />
      <meta property="og:description" content="Learn about Copper Boss Technology, your passionate partner for innovative IT solutions." />
      <meta property="og:image" content="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80" />
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content="https://www.yourwebsite.com/about" /> {/* <-- IMPORTANT: Replace with your actual URL */}
      <meta property="twitter:title" content="About Us | Copper Boss Technology | Expert IT Solutions in Madurai" />
      <meta property="twitter:description" content="Learn about Copper Boss Technology, your passionate partner for innovative IT solutions." />
      <meta property="twitter:image" content="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80" />
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>

      <Box
        component="main"
        id="about-section"
        sx={{
          py: { xs: 8, md: 12 },
          backgroundColor: theme.palette.background.default,
          position: 'relative',
          overflow: 'hidden',
          padding: { xs: '100px 20px', md: '100px 20px' }
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                animation: `${fadeInDown} 1s ease`,
                fontWeight: 700,
                color: theme.palette.text.primary,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  width: 80,
                  height: 4,
                  backgroundColor: theme.palette.primary.main,
                  bottom: -15,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  borderRadius: 2,
                },
              }}
            >
              About Us
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: isMobile ? 0 : 2,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    border: `5px solid ${theme.palette.primary.main}`,
                    top: 25,
                    left: 25,
                    zIndex: 0,
                    borderRadius: theme.shape.borderRadius * 1.5,
                    display: isMobile ? 'none' : 'block',
                  },
                }}
              >
                <Box
                  component="img"
                  width="500"
                  height="333"
                  loading="lazy"
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60"
                  alt="The Copper Boss Technology team collaborating around a glass wall with sticky notes, planning innovative IT solutions."
                  sx={{
                    width: '100%',
                    maxWidth: 500,
                    height: 'auto',
                    borderRadius: theme.shape.borderRadius * 1.5,
                    position: 'relative',
                    zIndex: 1,
                    boxShadow: theme.shadows[6],
                    transition: theme.transitions.create('transform', {
                      duration: theme.transitions.duration.standard,
                    }),
                    '&:hover': {
                      transform: 'translate(-12px, -12px)',
                    },
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <AnimatedBox sx={{ animationDelay: '0.4s' }}>
              <Typography
  variant="body1"
  paragraph
  sx={{ mb: 3, lineHeight: 1.8, color: theme.palette.text.secondary }}
>
  At <Typography component="span" color="primary" fontWeight="600">Copper Boss Technology</Typography>, we provide 
  <strong> professional laptop repair services</strong>, <strong>custom software and website development</strong>, and 
  <strong> reliable IT support</strong> for businesses and individuals. We also specialize in <strong>premium laptop builds </strong> 
   and <strong>custom high-performance PC builds</strong> tailored to business, creative, and gaming needs. 
  Our goal is to deliver <strong>fast, affordable, and high-quality technology solutions</strong> that keep you productive and ahead of the competition.
</Typography>

<Typography
  variant="body1"
  paragraph
  sx={{ mb: 3, lineHeight: 1.8, color: theme.palette.text.secondary }}
>
  Our skilled team focuses on <strong>IT infrastructure optimization</strong>, <strong>cybersecurity enhancement</strong>, and 
  <strong>operational efficiency improvement</strong>. Whether you need system monitoring, hardware upgrades, 
  premium laptop servicing, a secure business website, or a powerful custom-built PC, 
  <Typography component="span" color="primary" fontWeight="600">Copper Boss Technology</Typography> offers expert, 
  SEO-friendly, and future-ready solutions that empower your business to reach its full potential in today’s competitive digital world.
</Typography>

              </AnimatedBox>

              <Box sx={{ mt: { xs: 4, md: 6 } }}>
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    mb: 4,
                    fontWeight: 700,
                    color: 'text.primary',
                    position: 'relative',
                    display: 'inline-block',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      width: '80%',
                      height: 4,
                      backgroundColor: 'primary.main',
                      bottom: -10,
                      left: '10%',
                      borderRadius: 2,
                    },
                  }}
                >
                  Our Advantages
                </Typography>

                <Grid
                  container
                  spacing={2}
                  component={motion.div}
                  variants={cardContainerVariant}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                >
                  <Grid item xs={12} sm={6} md={4} component={motion.div} variants={cardItemVariant}>
                    <EnhancedFeatureCard>
                      <CardContent sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                        <FeatureIcon variant="h4" component="span" role="img" aria-label="Professional Tools Icon">🛠️</FeatureIcon>
                        <FeatureTitle variant="h6">Professional Tools</FeatureTitle>
                        <FeatureDescription variant="body2">
                          State-of-the-art equipment for all your IT needs.
                        </FeatureDescription>
                      </CardContent>
                    </EnhancedFeatureCard>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} component={motion.div} variants={cardItemVariant}>
                    <EnhancedFeatureCard>
                      <CardContent sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                        <FeatureIcon variant="h4" component="span" role="img" aria-label="Quick Delivery Icon">⚡</FeatureIcon>
                        <FeatureTitle variant="h6">Quick Delivery</FeatureTitle>
                        <FeatureDescription variant="body2">
                          Timely service without compromising quality or business continuity.
                        </FeatureDescription>
                      </CardContent>
                    </EnhancedFeatureCard>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} component={motion.div} variants={cardItemVariant}>
                    <EnhancedFeatureCard>
                      <CardContent sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                        <FeatureIcon variant="h4" component="span" role="img" aria-label="24/7 Support Icon">⏰</FeatureIcon>
                        <FeatureTitle variant="h6">24/7 Support</FeatureTitle>
                        <FeatureDescription variant="body2">
                          Round-the-clock assistance whenever you need it.
                        </FeatureDescription>
                      </CardContent>
                    </EnhancedFeatureCard>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} component={motion.div} variants={cardItemVariant}>
                    <EnhancedFeatureCard>
                      <CardContent sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                        <FeatureIcon variant="h4" component="span" role="img" aria-label="Fair Prices Icon">💰</FeatureIcon>
                        <FeatureTitle variant="h6">Fair Prices</FeatureTitle>
                        <FeatureDescription variant="body2">
                          Competitive pricing with absolutely no hidden fees.
                        </FeatureDescription>
                      </CardContent>
                    </EnhancedFeatureCard>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} component={motion.div} variants={cardItemVariant}>
                    <EnhancedFeatureCard>
                      <CardContent sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                        <FeatureIcon variant="h4" component="span" role="img" aria-label="Certified Experts Icon">🎓</FeatureIcon>
                        <FeatureTitle variant="h6">Certified Experts</FeatureTitle>
                        <FeatureDescription variant="body2">
                          Our experienced professionals handle complex hardware and software issues.
                        </FeatureDescription>
                      </CardContent>
                    </EnhancedFeatureCard>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} component={motion.div} variants={cardItemVariant}>
                    <EnhancedFeatureCard>
                      <CardContent sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                        <FeatureIcon variant="h4" component="span" role="img" aria-label="Complete Solutions Icon">🔗</FeatureIcon>
                        <FeatureTitle variant="h6">Complete Solutions</FeatureTitle>
                        <FeatureDescription variant="body2">
                          From development to repair, we are your single-point IT partner.
                        </FeatureDescription>
                      </CardContent>
                    </EnhancedFeatureCard>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>

          <Box
            sx={{
              textAlign: 'center',
              mt: { xs: 8, md: 12 },
              animation: `${fadeIn} 1s ease 0.8s forwards`,
              opacity: 0,
              py: 4,
            }}
          >
            <Box
              sx={{
                textAlign: 'center',
                px: 3,
                py: 6,
                background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
                borderRadius: 4,
                boxShadow: theme.shadows[6],
                maxWidth: 800,
                mx: 'auto',
                border: `1px solid ${alpha(theme.palette.primary.light, 0.2)}`,
                position: 'relative',
                overflow: 'hidden',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: -50,
                  right: -50,
                  width: 200,
                  height: 200,
                  background: `radial-gradient(circle, ${alpha(theme.palette.primary.light, 0.1)} 0%, transparent 70%)`,
                  zIndex: 0
                }
              }}
            >
              <Box sx={{
                position: 'absolute',
                bottom: -20,
                left: -20,
                width: 100,
                height: 100,
                background: `radial-gradient(circle, ${alpha(theme.palette.secondary.light, 0.1)} 0%, transparent 70%)`,
                zIndex: 0
              }} />

              <Box position="relative" zIndex={1}>
                <Typography
                  variant="h4"
                  component="h2"
                  sx={{
                    mb: 3,
                    fontWeight: 800,
                    background: `linear-gradient(45deg, ${theme.palette.text.primary} 30%, ${theme.palette.primary.main} 90%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'inline-block',
                    letterSpacing: -0.5,
                    lineHeight: 1.2,
                    [theme.breakpoints.up('md')]: {
                      fontSize: '2.2rem'
                    }
                  }}
                >
                  Elevate Your Business With Our Professional Services
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    mb: 5,
                    maxWidth: 600,
                    mx: 'auto',
                    color: theme.palette.text.secondary,
                    lineHeight: 1.8,
                    fontSize: '1.1rem',
                    position: 'relative',
                    '&:after': {
                      content: '""',
                      display: 'block',
                      width: 80,
                      height: 4,
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      borderRadius: 2,
                      mx: 'auto',
                      mt: 4
                    }
                  }}
                >
                  Let's transform your vision into reality. Contact Copper Boss Technology today and unlock your business's full potential with our tailored solutions.
                </Typography>

                <Box
                  sx={{
                    display: 'inline-flex',
                    position: 'relative',
                    '&:hover': {
                      '& .button-glow': {
                        opacity: 1
                      }
                    }
                  }}
                >
                  <Box
                    className="button-glow"
                    sx={{
                      position: 'absolute',
                      top: -10,
                      left: -10,
                      right: -10,
                      bottom: -10,
                      background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.4)} 0%, transparent 70%)`,
                      borderRadius: '60px',
                      opacity: 0,
                      transition: theme.transitions.create('opacity', {
                        duration: theme.transitions.duration.standard,
                      }),
                      zIndex: -1
                    }}
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    sx={{
                      px: 7,
                      py: 2,
                      borderRadius: '50px',
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      textTransform: 'none',
                      fontSize: '1.1rem',
                      background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      boxShadow: `${theme.shadows[4]}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.2)}`,
                      transition: theme.transitions.create(
                        ['transform', 'box-shadow', 'background'],
                        {
                          duration: theme.transitions.duration.standard,
                        }
                      ),
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: `${theme.shadows[10]}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.2)}`,
                        background: `linear-gradient(45deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      },
                      '&:active': {
                        transform: 'translateY(1px)',
                        boxShadow: theme.shadows[4]
                      },
                      '&:focus-visible': {
                        outline: `3px solid ${alpha(theme.palette.primary.light, 0.5)}`,
                        outlineOffset: 2
                      }
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        mr: 1.5,
                        display: 'inline-flex',
                        transition: theme.transitions.create('transform', {
                          duration: theme.transitions.duration.shorter,
                        })
                      }}
                    >
                      👋
                    </Box>
                    Schedule a Consultation
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default AboutUs;